import React from "react";

import { orderBy } from "lodash";

import { TradingGroupId } from "@fartherfinance/frontend/api/Types";

import {
  ACCOUNT_UNDER_10_000,
  TARGET_WEIGHT_TOOLTIP_TEXT,
  WEIGHT_DIFFERENCE_TOOLTIP_TEXT,
} from "../../constants";
import { useCreateEditTaxBudgetContext } from "../../reducer/Context";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import TooltipBasic from "@src/sharedComponents/Tooltip/TooltipBasic";
import toPercent from "@src/utils/toPercent";

import SecurityRowV2 from "./SecurityRowV2";

import styles2 from "./HypotheticalHoldingsWeightsTable.module.css";
import styles from "../basicTable.module.css";

const HypotheticalHoldingsWeightsTableV2: React.FC<{
  isAnAccountUnder10_000: boolean;
  selectedTradingGroup: TradingGroupId;
}> = ({ isAnAccountUnder10_000, selectedTradingGroup }) => {
  const { state } = useCreateEditTaxBudgetContext();

  const {
    data: { hypotheticalProposalV2 },
  } = state;

  const securities = Object.values(
    hypotheticalProposalV2?.hypotheticalTradesTable[selectedTradingGroup] || {}
  );

  const sortedSecurities = orderBy(
    securities,
    (sec) => sec.preWeight.toNumber(),
    "desc"
  );

  return (
    <>
      <div className={styles2.heading}>
        <div className={styles2.header}>
          Holdings Weights: Deviation from Target Model Due to Tax Budget
        </div>

        {isAnAccountUnder10_000 && (
          <TooltipBasic
            className={styles.infoIcon}
            text={ACCOUNT_UNDER_10_000}
          />
        )}
      </div>

      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr className={styles.headerRow}>
            <td
              className={toClassName(styles.headerCell, styles2.textAlignLeft)}
            >
              Holdings
            </td>

            <td
              className={toClassName(styles.headerCell, styles2.textAlignLeft)}
            >
              Current
            </td>

            <td className={styles.headerCell}>
              <div
                className={toClassName(styles.flexRow, styles2.textAlignLeft)}
              >
                <div>Target</div>

                <TooltipBasic
                  className={styles.infoIcon}
                  type={"secondary"}
                  text={TARGET_WEIGHT_TOOLTIP_TEXT}
                />
              </div>
            </td>

            <td
              className={toClassName(styles.headerCell, styles2.textAlignLeft)}
            >
              Without Tax Budget
            </td>

            <td
              className={toClassName(styles.headerCell, styles2.textAlignLeft)}
            >
              With Tax Budget
            </td>

            <td className={styles.headerCell}>
              <div
                className={toClassName(styles.flexRow, styles2.textAlignLeft)}
              >
                <div>Difference</div>

                <TooltipBasic
                  className={styles.infoIcon}
                  type={"secondary"}
                  text={WEIGHT_DIFFERENCE_TOOLTIP_TEXT}
                />
              </div>
            </td>
          </tr>
        </thead>

        <tbody>
          {sortedSecurities.map((sec) => {
            return (
              <SecurityRowV2
                key={sec.securityId}
                securityId={sec.securityId}
                current={toPercent(sec.preWeight.toNumber())}
                target={toPercent(sec.targetWeight.toNumber())}
                withTaxBudget={toPercent(
                  sec.postWeightWithTaxBudget.toNumber()
                )}
                withoutTaxBudget={toPercent(
                  sec.postWeightWithoutTaxBudget.toNumber()
                )}
                difference={toPercent(sec.portfolioWeightDelta.toNumber())}
              />
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default HypotheticalHoldingsWeightsTableV2;
